<script lang="ts">
  import { onMount } from "svelte";
  import Table from "../../Table.svelte";
  import Button from "../../Button.svelte";
  import DateRange from "../../DateRange.svelte";
  import Pagination from "../../Pagination.svelte";
  import SearchInput from "../../SearchInput.svelte";
  import { push } from "svelte-spa-router";
  import {
    CharityDonationRequestService,
    type CharityDonationRequest,
    type PaginationInfo,
  } from "../../../lib/api/charityServices";
  import { setCurrentDonation } from "../../../lib/stores/donation-request.store";
  import {
    getCharityDonationDetailStatus,
    type CharityDonationDetailStatus,
  } from "../../../lib/utils/charity-donation-status";
  import { getStageMeta, type LifecycleStage, type LifecycleTone } from "../../../lib/donationLifecycle";

  // Canonical lifecycle status badge as an HTML string (icon by tone, matches StageBadge.svelte).
  function stageBadgeIcon(tone: LifecycleTone): string {
    if (tone === 'red') {
      return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>';
    }
    if (tone === 'amber' || tone === 'grey') {
      return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>';
    }
    return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>';
  }

  // Map the grouped charity status to a canonical lifecycle stage. The list only shows
  // employer-paid donations, so the charity is either awaiting (paid_to_charity) or has received.
  function stageForCharityStatus(status: CharityStatus): LifecycleStage {
    return status === 'Paid - Received' ? 'received_by_charity' : 'paid_to_charity';
  }

  // Grouped by employer: one row per employer with summed hours/value (employee not shown)
  interface EmployerDonationSummary {
    employer_uuid: string;
    employer_name: string;
    total_hours: number;
    total_value: number;
    request_count: number;
    latest_date: string;
    status: CharityStatus;
    donations: CharityDonationRequest[];
  }

  // Charity-specific status types (used for grouping)
  type CharityStatus = CharityDonationDetailStatus;

  // State variables: raw donations (for grouping), display = employer summaries
  let donations: CharityDonationRequest[] = [];
  let employerSummaries: EmployerDonationSummary[] = [];
  let paging: PaginationInfo | null = null;
  let loading = false;
  let error: string | null = null;

  // Filter states
  let searchQuery = "";
  let startDate = "";
  let endDate = "";

  // Pagination over employer list (not raw donations)
  let currentPage = 1;
  let itemsPerPage = 10;
  /** Backend limits per_page to 100; we paginate to fetch all. */
  const FETCH_PAGE_SIZE = 100;
  const MAX_FETCH_PAGES = 50;

  // Load initial data
  onMount(async () => {
    await Promise.all([
      // loadEmployers(),
      loadDonationRequests(),
    ]);
  });

  // Load employers for dropdown
  // async function loadEmployers() {
  //   try {
  //     console.log('Loading employers...');
  //     const result = await EmployerUserService.getEmployerUsers() as unknown as ExtendedEmployerUsersResponse;
  //     console.log('Employers result:', result);

  //     if (result.success && result.employerUsers) {
  //       console.log('Raw employer users:', result.employerUsers);

  //       const mappedEmployers = result.employerUsers.employer.map((user: EmployerUser) => {
  //         // Try different ways to get the name
  //         let name = 'Unknown';

  //         if (user.name) {
  //           name = user.name;
  //         } else if (user.full_name) {
  //           name = user.full_name;
  //         } else if (user.employer?.name) {
  //           name = user.employer.name;
  //         } else if (user.user?.name) {
  //           name = user.user.name;
  //         } else if (user.user?.full_name) {
  //           name = user.user.full_name;
  //         }

  //         console.log('Mapping user:', user, 'to name:', name);

  //         return {
  //           uuid: user.uuid,
  //           name: name
  //         };
  //       }).filter((emp: {uuid: string, name: string}) => emp.name !== 'Unknown' && emp.name !== 'All Employers'); // Filter out any with unknown names or duplicates

  //       // Reset the array completely and rebuild it
  //       employerOptions = [
  //         ...mappedEmployers
  //       ];

  //       console.log('Final employer options:', employerOptions);
  //     } else {
  //       console.error('Failed to load employer users:', result.error);
  //       employerOptions = [{ uuid: '', name: 'All Employers' }];
  //     }
  //   } catch (err) {
  //     console.error('Error loading employers:', err);
  //     employerOptions = [{ uuid: '', name: 'All Employers' }];
  //   }
  // }

  function getCharityStatusForDonation(donation: CharityDonationRequest): CharityStatus {
    return getCharityDonationDetailStatus(donation);
  }

  function isEmployerPaidDonation(donation: CharityDonationRequest): boolean {
    return donation.lifecycle_stage === 'employer_paid';
  }

  /** Group donations by employer and sum hours/value. One row per employer. */
  function groupDonationsByEmployer(allDonations: CharityDonationRequest[]): EmployerDonationSummary[] {
    const byEmployer = new Map<string, CharityDonationRequest[]>();
    for (const d of allDonations) {
      const key = d.employer?.uuid || (d as any).employer_uuid || 'unknown';
      if (!byEmployer.has(key)) byEmployer.set(key, []);
      byEmployer.get(key)!.push(d);
    }
    const summaries: EmployerDonationSummary[] = [];
    for (const [employerUuid, list] of byEmployer) {
      const totalHours = list.reduce((s, d) => s + (d.leave_hours ?? d.hours ?? 0), 0);
      const totalValue = list.reduce((s, d) => s + (d.leave_value ?? d.value ?? 0), 0);
      const dates = list.map((d) => d.created_at || d.date || '').filter(Boolean);
      const latestDate = dates.length ? dates.sort().reverse()[0] : '';
      const statuses = list.map(getCharityStatusForDonation);
      const status: CharityStatus =
        statuses.some((s) => s === 'Paid - Received')
          ? (statuses.some((s) => s !== 'Paid - Received') ? 'Waiting for Payment' : 'Paid - Received')
          : statuses.some((s) => s === 'Waiting for Payment')
            ? 'Waiting for Payment'
            : 'Pending';
      const name = list[0]?.employer?.name ?? (list[0] as any)?.employer_name ?? 'Unknown Employer';
      summaries.push({
        employer_uuid: employerUuid === 'unknown' ? '' : employerUuid,
        employer_name: name,
        total_hours: totalHours,
        total_value: totalValue,
        request_count: list.length,
        latest_date: latestDate,
        status,
        donations: list,
      });
    }
    return summaries.sort((a, b) => (b.latest_date || '').localeCompare(a.latest_date || ''));
  }

  // Load donation requests: fetch all pages, then group by employer
  async function loadDonationRequests() {
    loading = true;
    error = null;

    try {
      let allDonations: CharityDonationRequest[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const result = await CharityDonationRequestService.getDonationRequests({
          search: searchQuery || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          page,
          per_page: FETCH_PAGE_SIZE,
        });

        if (!result.success) {
          error = result.error || "Failed to load donation requests";
          donations = [];
          employerSummaries = [];
          paging = null;
          return;
        }

        const chunk = result.donations || [];
        allDonations = allDonations.concat(chunk);
        const totalPages = result.paging?.last_page ?? 1;
        hasMore = page < totalPages && page < MAX_FETCH_PAGES;
        page += 1;
      }

      donations = allDonations.filter(isEmployerPaidDonation);
      employerSummaries = groupDonationsByEmployer(donations);
      const totalEmployers = employerSummaries.length;
      paging = {
        current_page: currentPage,
        last_page: Math.max(1, Math.ceil(totalEmployers / itemsPerPage)),
        per_page: itemsPerPage,
        total: totalEmployers,
        from: totalEmployers === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
        to: Math.min(currentPage * itemsPerPage, totalEmployers),
      };
    } catch (err) {
      console.error("Error in loadDonationRequests:", err);
      error = "An error occurred while loading donation requests";
      donations = [];
      employerSummaries = [];
      paging = null;
    } finally {
      loading = false;
    }
  }

  // function handleEmployerFilter(value: string) {
  //   selectedEmployer = value;
  //   // Filtering happens only when search button is clicked
  // }

  // Handle date range changes
  function handleDateChange(event: CustomEvent) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    // Filtering happens only when search button is clicked
  }

  function handleSearch() {
    currentPage = 1; // Reset to first page
    loadDonationRequests();
  }

  // Handle search input
  function handleSearchInput(event: CustomEvent) {
    searchQuery = event.detail;
    currentPage = 1; // Reset to first page
    loadDonationRequests();
  }

  // Handle pagination
  // Paginated slice of employer summaries for current page (no refetch on page change)
  $: paginatedEmployers = employerSummaries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  $: employerLastPage = Math.max(1, Math.ceil(employerSummaries.length / itemsPerPage));

  function handlePageChange(event: CustomEvent) {
    const page = event.detail.page;
    if (page !== currentPage) {
      currentPage = page;
    }
  }

  function formatDate(dateString: string | undefined) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  function formatCurrency(value: number | undefined) {
    if (value === undefined) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }

  // Table columns: employer-only (no employee). Row = EmployerDonationSummary.
  let columns = [
    {
      key: "employer_name",
      label: "Employer",
      width: "35%",
      mobilePrimary: true,
      render: (_value: any, row: EmployerDonationSummary) => {
        return `<span class="employer-name">${row.employer_name}</span>`;
      },
    },
    {
      key: "total_hours",
      label: "Total Hours",
      width: "15%",
      render: (_value: any, row: EmployerDonationSummary) => {
        return `${row.total_hours} hrs`;
      },
    },
    {
      key: "total_value",
      label: "Total Value",
      width: "15%",
      render: (_value: any, row: EmployerDonationSummary) => {
        return formatCurrency(row.total_value);
      },
    },
    {
      key: "request_count",
      label: "Requests",
      width: "10%",
      render: (_value: any, row: EmployerDonationSummary) => {
        return String(row.request_count);
      },
    },
    {
      key: "latest_date",
      label: "Latest",
      width: "12%",
      render: (_value: any, row: EmployerDonationSummary) => {
        return formatDate(row.latest_date);
      },
    },
    {
      key: "status",
      label: "Status",
      width: "13%",
      align: 'center' as const,
      render: (_value: any, row: EmployerDonationSummary) => {
        const meta = getStageMeta(stageForCharityStatus(row.status));
        return `<span class="lc-badge ${meta.badgeClass}">${stageBadgeIcon(meta.tone)}<span>${meta.label}</span></span>`;
      },
    },
  ];

  function handleRowClick(row: EmployerDonationSummary) {
    if (!row?.donations?.length) return;
    const awaiting = row.donations.filter(
      (d) => getCharityStatusForDonation(d) === 'Waiting for Payment'
    );
    const donation = (awaiting.length ? awaiting : row.donations)[0];
    const uuid = donation?.uuid ?? (donation as any)?.id;
    if (uuid) {
      setCurrentDonation(donation);
      push(`/donation-request/${uuid}`);
    }
  }

</script>

<div class="donation-requests-container">
  <!-- Header with filters and search -->
  <div class="page-header">
    <div class="filters-section">
      <div class="filter-item search-item">
        <SearchInput
          placeholder="Search"
          bind:value={searchQuery}
          width="100%"
          on:search={handleSearchInput}
        />
      </div>

      <!-- <div class="filter-item">
      <Dropdown
        options={employerOptions}
        bind:selectedValue={selectedEmployer}
        placeholder="All Employers"
        onSelectionChange={handleEmployerFilter}
      />
        </div> -->

      <div class="filter-item date-range-wrapper">
        <DateRange
          bind:startDate
          bind:endDate
          placeholder="Date Range"
          on:change={handleDateChange}
        />
      </div>
    </div>

    <div class="search-section">
      <Button variant="primary" on:click={handleSearch} disabled={loading}>
        Search
      </Button>
    </div>
  </div>

  <!-- Loading State -->
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donation requests...</p>
    </div>
  {/if}

  <!-- Error State -->
  {#if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={loadDonationRequests}>
        Try Again
      </Button>
    </div>
  {/if}

  <!-- Debugging: Show raw data -->
  <div class="debug-container" style="display: none;">
    <pre>{JSON.stringify(donations, null, 2)}</pre>
  </div>

  <!-- Using the Table Component: one row per employer (grouped/summed), no employee -->
  {#if !loading && !error && employerSummaries.length > 0}
    <Table {columns} data={paginatedEmployers} onRowClick={handleRowClick} />

    <!-- Pagination over employer list -->
    {#if employerLastPage > 1}
      <div class="pagination-wrapper">
        <Pagination
          {currentPage}
          totalPages={employerLastPage}
          maxVisiblePages={7}
          on:pageChange={handlePageChange}
        />
      </div>
    {/if}
  {:else if !loading && !error}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" width="48" height="48" fill="#9ca3af">
        <path
          d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"
        />
        <path d="M12 12.59l-2.3-2.3-1.4 1.42L12 15.41l5.7-5.7-1.4-1.42z" />
      </svg>
      <p>No donation requests found</p>
      <Button variant="primary" on:click={handleSearch}>Reset Filters</Button>
    </div>
  {/if}
</div>

<style>
  :root {
    --color-primary: #f0b429;
    --color-primary-hover: #d69e20;
  }

  .donation-requests-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: calc(100vh - 70px);
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-section {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
    flex-wrap: wrap;
  }

  .filter-item {
    flex: 0 0 auto;
    min-width: 180px;
    max-width: 200px;
  }

  .filter-item.search-item {
    min-width: 200px;
    max-width: 250px;
    flex: 1;
  }

  .date-range-wrapper {
    max-width: 250px;
  }

  .date-range-wrapper :global(.daterange-container) {
    width: 100% !important;
  }

  .search-section {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .empty-state p {
    color: #6b7280;
    font-size: 1.1rem;
    margin: 1rem 0 1.5rem;
  }

  .debug-container {
    background-color: #f3f4f6;
    padding: 1rem;
    border-radius: 8px;
    overflow: auto;
    max-height: 200px;
    font-family: monospace;
    font-size: 12px;
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }

  /* Global styles for rendered content in table */
  :global(.charity-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.employee-name) {
    font-weight: 500;
    color: #374151;
  }

  :global(.employer-link) {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: none;
    border: none;
    padding: 0;
    color: var(--color-primary, #f0b429);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
  }

  :global(.employer-link:hover) {
    color: var(--color-primary-hover, #d69e20);
    text-decoration: underline;
  }

  :global(.employer-link:focus) {
    outline: none;
  }

  :global(.view-btn-donation-charity) {
    background: #111827 !important;
    color: white !important;
    border: none !important;
    border-radius: 50px !important;
    padding: 0.5rem 1rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: background-color 0.2s !important;
  }

  :global(.view-btn-donation-charity:hover) {
    background: #1f2937;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
  }

  :global(.status-badge svg) {
    width: 16px;
    height: 16px;
    margin-right: 0.25rem;
    flex-shrink: 0;
    display: block;
  }

  :global(.status-badge span) {
    display: block;
    margin: 0;
    padding: 0;
  }

  :global(.status-badge.pending) {
    background-color: #fff8e1;
    color: #f57c00;
  }

  :global(.status-badge.approved) {
    background-color: #e6f4ea;
    color: #34a853;
  }

  :global(.status-badge.rejected) {
    background-color: #fdedeb;
    color: #ea4335;
  }

  :global(.status-badge.completed) {
    background-color: #e8f0fe;
    color: #007aff;
  }

  :global(.status-badge.incoming),
  :global(.status-badge.pending) {
    background-color: #fff8e1;
    color: #f57c00;
  }

  :global(.status-badge.waiting-payment) {
    background-color: #fff8e1;
    color: #f57c00;
  }

  :global(.status-badge.paid-received) {
    background-color: #e6f4ea;
    color: #34a853;
  }

  /* Responsive design */
  @media (max-width: 1280px) {
    .page-header {
      flex-wrap: wrap;
      gap: 1rem;
    }

    .filters-section {
      flex: 1 1 100%;
      justify-content: flex-start;
      margin-bottom: 0.5rem;
    }

    .search-section {
      flex: 0 0 auto;
      width: auto;
      margin-left: auto;
    }
  }

  @media (max-width: 1024px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }

    .filters-section {
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .filter-item {
      flex: 1 1 calc(33% - 0.75rem);
      min-width: 180px;
      max-width: none;
    }

    .date-range-wrapper {
      max-width: none;
    }

    .search-section {
      width: 100%;
      justify-content: flex-end;
    }
  }

  @media (max-width: 768px) {
    .donation-requests-container {
      padding: 1rem;
    }

    .page-header {
      padding: 1rem;
    }

    .filters-section {
      flex-direction: column;
      gap: 0.75rem;
    }

    .filter-item {
      width: 100%;
      min-width: 100%;
      max-width: none;
    }

    .search-section {
      width: 100%;
    }

    .search-section :global(button) {
      width: 100%;
    }

    :global(.dropdown-container) {
      width: 100% !important;
    }

    :global(.daterange-container) {
      width: 100% !important;
    }
  }
</style>
